import {
  memo,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type TouchEvent as ReactTouchEvent,
} from "react";
import { useTranslation } from "react-i18next";
import {
  Maximize,
  Minimize,
  Pause,
  PictureInPicture2,
  Play,
  Volume2,
  VolumeX,
} from "lucide-react";
import clsx from "clsx";
import { SettingsMenu, type QualityMenuState } from "./SettingsMenu";
import { formatPlayerTime } from "@/utils/duration";

export interface VideoControlBarProps {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  /** End of the buffered range containing currentTime; null when unknown. */
  bufferedEnd: number | null;
  volume: number;
  isMuted: boolean;
  playbackRate: number;
  isFullscreen: boolean;
  pip: { supported: boolean; active: boolean };
  quality: QualityMenuState;
  /** Transport is unavailable (recording gap, HLS not ready yet). */
  disabled?: boolean;
  variant?: "default" | "compact";
  visible: boolean;
  /** Player-specific buttons (e.g. the analytics player's Restart). */
  extraControls?: ReactNode;
  onPlayPause: () => void;
  onSeek: (time: number) => void;
  /**
   * Receives the raw pointer event because SynchronizedVideoPlayer needs the
   * press coordinates to tell a click apart from a drag before it commits a
   * seek to the shared telemetry cursor. Consumers that don't care can ignore
   * the argument.
   */
  onSeekStart?: (
    event:
      | ReactMouseEvent<HTMLInputElement>
      | ReactTouchEvent<HTMLInputElement>,
  ) => void;
  onSeekMove?: (event: ReactMouseEvent<HTMLInputElement>) => void;
  onSeekEnd?: () => void;
  onVolumeChange: (volume: number) => void;
  onToggleMute: () => void;
  onPlaybackRateChange: (rate: number) => void;
  onQualityChange: (height: number | null) => void;
  onToggleFullscreen: () => void;
  onTogglePip: () => void;
}

/**
 * Presentational transport bar. Holds no video state and never touches a
 * media element — every value arrives as a prop and every gesture leaves as a
 * callback. That is what lets one bar serve both a plain <video> (via
 * useVideoControls) and SynchronizedVideoPlayer, whose time axis is the
 * telemetry session clock rather than the media's own currentTime.
 */
export const VideoControlBar = memo(function VideoControlBar({
  isPlaying,
  currentTime,
  duration,
  bufferedEnd,
  volume,
  isMuted,
  playbackRate,
  isFullscreen,
  pip,
  quality,
  disabled = false,
  variant = "default",
  visible,
  extraControls,
  onPlayPause,
  onSeek,
  onSeekStart,
  onSeekMove,
  onSeekEnd,
  onVolumeChange,
  onToggleMute,
  onPlaybackRateChange,
  onQualityChange,
  onToggleFullscreen,
  onTogglePip,
}: VideoControlBarProps) {
  const { t } = useTranslation("pages");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isCompact = variant === "compact";

  // A seekable range needs a positive duration; without one the slider would
  // pin to its default 0..100 and report meaningless times.
  const seekMax = duration > 0 ? duration : 0;
  const progressPercent = seekMax > 0 ? (currentTime / seekMax) * 100 : 0;
  const bufferedPercent =
    seekMax > 0 && bufferedEnd !== null
      ? Math.min(100, (bufferedEnd / seekMax) * 100)
      : 0;

  return (
    <div
      data-testid="video-control-bar"
      className={clsx(
        "absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent transition-opacity duration-200",
        isCompact ? "px-2 pb-1.5 pt-6" : "px-3 pb-2 pt-8",
        // Pinned open while a menu is showing, otherwise the bar would fade
        // out from under the popover the viewer is reading.
        visible || isMenuOpen
          ? "opacity-100"
          : "pointer-events-none opacity-0",
      )}
    >
      <div className="relative mb-1">
        <div
          className="pointer-events-none absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 overflow-hidden rounded-full bg-white/25"
          aria-hidden="true"
        >
          <div
            className="h-full bg-white/40"
            style={{ width: `${bufferedPercent}%` }}
          />
          <div
            className="absolute inset-y-0 left-0 bg-[#60a5fa]"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        <input
          type="range"
          min={0}
          max={seekMax || 100}
          step="any"
          value={currentTime}
          disabled={disabled || seekMax === 0}
          onChange={(event) => onSeek(Number.parseFloat(event.target.value))}
          onMouseDown={onSeekStart}
          onMouseMove={onSeekMove}
          onMouseUp={onSeekEnd}
          onTouchStart={onSeekStart}
          onTouchEnd={onSeekEnd}
          aria-label={t("sessions.videoControls.seek")}
          aria-valuetext={formatPlayerTime(currentTime)}
          className="video-seek-slider relative w-full cursor-pointer appearance-none bg-transparent disabled:cursor-not-allowed"
        />
      </div>

      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onPlayPause}
            disabled={disabled}
            aria-label={
              isPlaying
                ? t("sessions.videoControls.pause")
                : t("sessions.videoControls.play")
            }
            className="rounded p-2 text-white transition-colors hover:text-[#60a5fa] disabled:cursor-not-allowed disabled:opacity-40"
          >
            {isPlaying ? (
              <Pause className="h-5 w-5" aria-hidden="true" />
            ) : (
              <Play className="h-5 w-5" aria-hidden="true" />
            )}
          </button>

          {extraControls}

          <div className="group flex items-center">
            <button
              type="button"
              onClick={onToggleMute}
              aria-label={
                isMuted
                  ? t("sessions.videoControls.unmute")
                  : t("sessions.videoControls.mute")
              }
              className="rounded p-2 text-white transition-colors hover:text-[#60a5fa]"
            >
              {isMuted || volume === 0 ? (
                <VolumeX className="h-4 w-4" aria-hidden="true" />
              ) : (
                <Volume2 className="h-4 w-4" aria-hidden="true" />
              )}
            </button>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={isMuted ? 0 : volume}
              onChange={(event) =>
                onVolumeChange(Number.parseFloat(event.target.value))
              }
              aria-label={t("sessions.videoControls.volume")}
              className="video-volume-slider h-1 w-0 cursor-pointer appearance-none rounded-full bg-white/30 opacity-0 transition-all duration-200 focus:w-16 focus:opacity-100 group-hover:w-16 group-hover:opacity-100"
            />
          </div>

          <span className="ml-1 select-none text-xs tabular-nums text-white">
            {formatPlayerTime(currentTime)} / {formatPlayerTime(duration)}
          </span>
        </div>

        <div className="flex items-center gap-1">
          <SettingsMenu
            quality={quality}
            playbackRate={playbackRate}
            onQualityChange={onQualityChange}
            onPlaybackRateChange={onPlaybackRateChange}
            onOpenChange={setIsMenuOpen}
            disabled={quality.isLoading && quality.levels.length === 0}
          />

          {pip.supported && (
            <button
              type="button"
              onClick={onTogglePip}
              aria-label={
                pip.active
                  ? t("sessions.videoControls.exitPip")
                  : t("sessions.videoControls.pip")
              }
              title={
                pip.active
                  ? t("sessions.videoControls.exitPip")
                  : t("sessions.videoControls.pip")
              }
              className="rounded p-2 text-white transition-colors hover:text-[#60a5fa]"
            >
              <PictureInPicture2 className="h-4 w-4" aria-hidden="true" />
            </button>
          )}

          <button
            type="button"
            onClick={onToggleFullscreen}
            aria-label={
              isFullscreen
                ? t("sessions.videoControls.exitFullscreen")
                : t("sessions.videoControls.fullscreen")
            }
            title={
              isFullscreen
                ? t("sessions.videoControls.exitFullscreen")
                : t("sessions.videoControls.fullscreen")
            }
            className="rounded p-2 text-white transition-colors hover:text-[#60a5fa]"
          >
            {isFullscreen ? (
              <Minimize className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Maximize className="h-4 w-4" aria-hidden="true" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
});

export default VideoControlBar;
